import React from "react";

import { Image } from "@nykaa/payment-ui/src/Image";

import {
  CouponCardWrapper,
  TopSection,
  LogoContainer,
  ContentContainer,
  SavingsText,
  DescriptionText,
  DividerRow,
  DividerLine,
  BottomRow,
  CouponCodeContainer,
  CouponCodeText,
  ApplyButton,
  ApplyButtonText,
  BgIllustrationWrapper,
  OverlapIllustrationWrapper,
} from "./assets/CouponCard.styles";
import { CouponCardProps } from "./CouponCard.types";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { getStringFromId } from "helpers/vernacular";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";


const CouponCardBgIllustrationUrl = getIcons(__PLATFORM__, ICONS_NAMES.COUPON_CARD_BG);
const CouponCardEmailIllustrationUrl = getIcons(__PLATFORM__, ICONS_NAMES.COUPON_CARD_EMAIL);


export const CouponCard: React.FC<CouponCardProps> = ({
  logoUrl,
  savingsText,
  description,
  couponCode,
  onCTAClick,
  onClick,
  isApplied = false,
  isDisabled = false,
  illustrationUrl1 = CouponCardBgIllustrationUrl,
  illustrationUrl2 = CouponCardEmailIllustrationUrl,
  notchBackgroundColor = "#ffffff",
}) => {
  const couponApplyCTA = getStringFromId(VERNACULAR_STRING_IDS.COUPON_APPLY_CTA, VERNACULAR_DEFAULT_MESSAGES.COUPON_APPLY_CTA);
  const couponRemoveCTA = getStringFromId(VERNACULAR_STRING_IDS.COUPON_REMOVE_CTA, VERNACULAR_DEFAULT_MESSAGES.COUPON_REMOVE_CTA);

  const handleCTAClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDisabled) {
      onCTAClick();
    }
  };

  const handleCardClick = () => {
    if (onClick && !isDisabled) {
      onClick();
    }
  };

  return (
    <CouponCardWrapper
      isDisabled={isDisabled}
      onClick={handleCardClick}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={`Coupon: ${savingsText}`}
      className="coupon-card"
    >
      <TopSection>
        <LogoContainer>
          <Image url={logoUrl || getIcons(__PLATFORM__, ICONS_NAMES.NYKAA_FALLBACK)} alt="" />
        </LogoContainer>

        <ContentContainer>
          <SavingsText>{savingsText}</SavingsText>
          <DescriptionText>{description}</DescriptionText>
        </ContentContainer>
        {illustrationUrl2 && (
          <OverlapIllustrationWrapper src={illustrationUrl2} alt="" />
        )}
      </TopSection>

      <DividerRow holeColor={notchBackgroundColor}>
        <DividerLine />
      </DividerRow>

      <BottomRow>
        <CouponCodeContainer>
          <CouponCodeText>{couponCode}</CouponCodeText>
        </CouponCodeContainer>

        <ApplyButton
          isApplied={isApplied}
          onClick={handleCTAClick}
          type="button"
          aria-label={isApplied ? `Remove coupon ${couponCode}` : `Apply coupon ${couponCode}`}
        >
          <ApplyButtonText>
            {isApplied ? couponRemoveCTA : couponApplyCTA}
          </ApplyButtonText>
        </ApplyButton>
      </BottomRow>
      {illustrationUrl1 && <BgIllustrationWrapper src={illustrationUrl1} alt="" />}
    </CouponCardWrapper>
  );
};
